import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

import authHook from '@hooks/useAuth'
import errorHandlerHook from '@hooks/useErrorHandler'
import windowFocusHook from '@hooks/useWindowFocus'

import AuthService from '@services/auth'

const EXCLUDED_ROUTES = ['/reactivate-account']

export type UserStatusCheckContextState = {
  isStatusCheckEnabled: boolean
  enableStatusCheck: () => void
  disableStatusCheck: () => void
}

export const UserStatusCheckContext =
  createContext<UserStatusCheckContextState>({} as UserStatusCheckContextState)

const isCurrentPathExcludedRoute = () => {
  const pathname = window.location.pathname
  return EXCLUDED_ROUTES.includes(pathname)
}

const UserStatusCheckProvider = ({ children }: { children: ReactNode }) => {
  const [isStatusCheckEnabled, setIsStatusCheckEnabled] = useState(true)
  const { admin, buyer, vendor } = authHook.useAuth()
  const { hasFocus } = windowFocusHook.useWindowFocus()
  const { handleError } = errorHandlerHook.useErrorHandler()
  const prevHasFocus = useRef(hasFocus)

  const enableStatusCheck = useCallback(() => {
    setIsStatusCheckEnabled(true)
  }, [])

  const disableStatusCheck = useCallback(() => {
    setIsStatusCheckEnabled(false)
  }, [])

  useEffect(() => {
    const isAuthenticated = !!(admin || buyer || vendor)

    if (!isAuthenticated || !isStatusCheckEnabled) {
      return
    }

    // Skip status check on specific routes
    if (isCurrentPathExcludedRoute()) {
      return
    }

    const windowGainedFocus = !prevHasFocus.current && hasFocus

    // Update the ref AFTER checking
    prevHasFocus.current = hasFocus

    if (windowGainedFocus) {
      AuthService.checkUserStatus().catch((err) => {
        handleError({
          exception: err,
        })
      })
    }
  }, [admin, buyer, vendor, isStatusCheckEnabled, hasFocus, handleError])

  return (
    <UserStatusCheckContext.Provider
      value={{
        isStatusCheckEnabled,
        enableStatusCheck,
        disableStatusCheck,
      }}
    >
      {children}
    </UserStatusCheckContext.Provider>
  )
}

export { UserStatusCheckProvider }
